import { useRequiredContext } from "@redotech/react-util/context";
import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import type {
  TrackingPageOrder,
  TrackingPageShipment,
} from "@redotech/redo-model/order-tracking/tracking-page-order-definition";
import { memo } from "react";
import { Flex } from "../../../flex";
import { PickupConfig } from "../../../settings-elements/order/pick-up-config-util";
import {
  ShipmentStatusDisplay,
  ShipmentStatusDisplayType,
} from "../../../settings-elements/order/shipment-status-display";
import { Text } from "../../../text";
import { TrackingPageContext } from "../../context/tracking-page-context";
import { getDeliveryDateStyles, getHeaderStyles } from "../shared/block-styles";
import {
  ExceptionalStatus,
  exceptionalStatusDisplayStrings,
  exceptionalStatusVerbiage,
} from "./shipment-exceptions";
import * as trackingCss from "./tracking-details.module.css";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;
const DAYS_OF_WEEK = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
] as const;

export const StatusDisplay = memo(function StatusDisplay({
  exceptionalStatus,
  pastPreTransit,
  delivery,
  order,
  shipment,
  pickupConfig,
  stalledDays,
}: {
  exceptionalStatus: { status: string } | undefined;
  pastPreTransit: boolean;
  delivery: {
    isDelivered: boolean;
    date: Date | null | undefined;
    eddDistrusted: boolean;
  };
  order: TrackingPageOrder;
  shipment?: TrackingPageShipment;
  pickupConfig: PickupConfig;
  stalledDays: number | null;
}) {
  const { returnAppSettings, pageSettings } =
    useRequiredContext(TrackingPageContext);

  const headerStyles = getHeaderStyles(pageSettings);
  const deliveryDateStyles = getDeliveryDateStyles(pageSettings);

  const renderDeliveryDate = () => {
    if (!pastPreTransit || delivery.isDelivered || !delivery.date) return null;

    return (
      <Flex dir="column" gap="xxs">
        <Text fontSize="md" fontWeight="thin" style={headerStyles}>
          {DAYS_OF_WEEK[delivery.date.getUTCDay()]},{" "}
          {MONTHS[delivery.date.getUTCMonth()]}
        </Text>
        <Text
          className={trackingCss.deliveryDate}
          fontWeight="bold"
          style={{ ...headerStyles, ...deliveryDateStyles }}
          textColor="primary"
        >
          {delivery.date.getUTCDate() ?? "Unknown"}
        </Text>
      </Flex>
    );
  };

  const renderStatus = () => (
    <>
      <Text fontSize="lg" fontWeight="semibold" style={headerStyles}>
        Status
      </Text>
      <Flex justify="space-between">
        <ShipmentStatusDisplay
          cancelReason={order.cancelReason}
          displayType={ShipmentStatusDisplayType.HEADER}
          headerStyles={headerStyles}
          pickupConfig={pickupConfig}
          shipment={shipment}
          source={order.sourceName}
          stalledDays={stalledDays}
          storeUrl={returnAppSettings.storeUrl}
        />
      </Flex>
    </>
  );

  if (exceptionalStatus) {
    const statusMessage =
      exceptionalStatusDisplayStrings[
        exceptionalStatus.status as ExceptionalStatus
      ];
    return (
      <Flex dir="column" mt="md">
        <Flex
          alignSelf="flex-start"
          bgColor="error-secondary"
          className={trackingCss.errorIcon}
          radius="full"
          textColor="error"
        >
          <AlertCircleIcon height="20" width="20" />
        </Flex>
        <Text fontSize="xl" fontWeight="bold" style={headerStyles}>
          {statusMessage}
        </Text>
        <Flex>
          <Text fontSize="sm">
            {exceptionalStatusVerbiage[
              exceptionalStatus.status as ExceptionalStatus
            ](returnAppSettings.merchantName)}
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <>
      {renderDeliveryDate()}
      {renderStatus()}
    </>
  );
});
